import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { AddBeneficiaryModal } from '@/components/order/AddBeneficiaryModal'
import { UserPlus, User, AlertCircle, CheckCircle2, Loader2, Clock, CalendarClock, ArrowLeft, SkipForward } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  getClientBeneficiaries,
  getBusinessClientBeneficiaries,
  updateOrderWithBeneficiary,
  createOrderFromTemplate,
  submitOrder,
  getOrderAggregatedSurvey,
  getEvaluationSnapshot,
} from '@/services/orderApi'
import { hasQuestions } from '@/utils/evaluationSnapshotTransformer'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { Beneficiary } from '@/services/orderApi'

export function BeneficiarySelectionPage() {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const {
    clientProfileId,
    businessClientProfileId,
    clientName,
    selectedTemplate,
    appointments,
    selectedBeneficiary,
    setSelectedBeneficiary,
    setIsSelfBeneficiary,
    setOrder,
    setEvaluationSnapshotId,
    setEvaluationSnapshot,
    setCurrentStep,
    getCreateOrderEntryPath,
  } = useOrderCreationStore()
  const orderSubjectProfileId = clientProfileId || businessClientProfileId

  // Advisory flag from the selected template: when the offering has no identifiable
  // care recipient (consultation, assessment, equipment-only), let the user move on
  // without picking a beneficiary.
  const canSkip = selectedTemplate?.requiresBeneficiary === false

  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState<'continue' | 'skip' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addBeneficiaryOpen, setAddBeneficiaryOpen] = useState(false)

  const loadBeneficiaries = useCallback(async (): Promise<Beneficiary[]> => {
    if (!orderSubjectProfileId) return []

    try {
      setLoading(true)
      setError(null)

      const data = businessClientProfileId
        ? await getBusinessClientBeneficiaries(businessClientProfileId)
        : clientProfileId
          ? await getClientBeneficiaries(clientProfileId)
          : []
      const rows = data.filter((b) => b.id)
      setBeneficiaries(rows)
      return rows
    } catch (err: any) {
      const errorMessage = err.message || t('orderCreation.failedToLoadBeneficiaries')

      console.error('Error loading beneficiaries:', err)
      setError(errorMessage)
      return []
    } finally {
      setLoading(false)
    }
  }, [
    businessClientProfileId,
    clientProfileId,
    orderSubjectProfileId,
    t,
  ])

  useEffect(() => {
    // Check prerequisites
    if (!orderSubjectProfileId) {
      navigate(getCreateOrderEntryPath(), { replace: true })
      return
    }
    if (!selectedTemplate) {
      navigate('/create-order/templates')
      return
    }
    if (appointments.length === 0) {
      navigate('/create-order/appointments')
      return
    }

    void loadBeneficiaries()
  }, [
    orderSubjectProfileId,
    selectedTemplate,
    appointments,
    navigate,
    getCreateOrderEntryPath,
    loadBeneficiaries,
  ])

  const handleSelectBeneficiary = (beneficiary: Beneficiary, isSelf: boolean = false) => {
    setSelectedBeneficiary(beneficiary)
    setIsSelfBeneficiary(isSelf)
  }

  const handleContinue = () => {
    if (!selectedBeneficiary) {
      setError(t('orderCreation.pleaseSelectBeneficiary'))
      return
    }
    void createOrder(selectedBeneficiary.id)
  }

  const handleSkip = () => {
    setSelectedBeneficiary(null)
    setIsSelfBeneficiary(false)
    void createOrder(null)
  }

  const createOrder = async (beneficiaryId: string | null) => {
    if (!selectedTemplate || !orderSubjectProfileId || creating) return

    try {
      setCreating(beneficiaryId ? 'continue' : 'skip')
      setError(null)

      const orderResponse = await createOrderFromTemplate({
        template_id: selectedTemplate.id,
        ...(businessClientProfileId
          ? { business_client_profile_id: businessClientProfileId }
          : { client_profile_id: orderSubjectProfileId }),
        scheduled_start: appointments[0].scheduledStart,
        appointments_schedule: appointments.map((apt) => ({
          appointment_template_id: apt.appointmentSpecId,
          scheduled_start: apt.scheduledStart,
        })),
        priority: 'normal',
      })

      const orderId = orderResponse.order.id
      const orderNumber = orderResponse.order.order_number
      setOrder(orderId, orderNumber)

      // Update order with beneficiary (skipped when the template doesn't need one)
      if (beneficiaryId) {
        await updateOrderWithBeneficiary(orderId, beneficiaryId)
      }

      // Submit order
      await submitOrder(orderId)

      // Get evaluation snapshot ID
      const surveyData = await getOrderAggregatedSurvey(orderId)
      if (surveyData.currentAggregatedSurveySnapshotId) {
        setEvaluationSnapshotId(surveyData.currentAggregatedSurveySnapshotId)
        
        // Check if snapshot has questions
        try {
          const snapshot = await getEvaluationSnapshot(orderId)
          if (snapshot && hasQuestions(snapshot)) {
            setEvaluationSnapshot(snapshot)
            setCurrentStep(6)
            navigate('/create-order/questions')
            return
          }
        } catch (err) {
          console.warn('Error checking for questions, proceeding to payment:', err)
        }
      }

      // No questions or no snapshot, skip directly to payment
      setCurrentStep(7)
      navigate('/create-order/payment')
    } catch (err: any) {
      const raw: string = err?.response?.data?.message || err?.response?.data?.error || err.message || ''
      setError(parseSchedulingError(raw) || t('orderCreation.failedToCreateOrder'))
      console.error('Error creating order:', err)
    } finally {
      setCreating(null)
    }
  }

  function formatMinutesAsHuman(totalMinutes: number): string {
    if (totalMinutes < 60) return `${totalMinutes} ${totalMinutes === 1 ? 'minute' : 'minutes'}`
    const hours = Math.floor(totalMinutes / 60)
    const mins = totalMinutes % 60
    const hourLabel = hours === 1 ? 'hour' : 'hours'
    if (mins === 0) return `${hours} ${hourLabel}`
    return `${hours} ${hourLabel} ${mins} min`
  }

  function parseSchedulingError(message: string): string | null {
    if (!message) return null
    if (/cannot be scheduled in the past/i.test(message)) {
      return t('orderCreation.appointmentInPast') + ' ' + t('orderCreation.schedulingErrorGoBack')
    }
    if (/must be booked at least/i.test(message)) {
      const match = message.match(/at least\s+(\d+)\s*minutes?/i)
      const mins = match?.[1] ? parseInt(match[1], 10) : 0
      const hours = mins > 0 ? formatMinutesAsHuman(mins) : '24 hours'
      return t('orderCreation.appointmentTooSoon', { hours }) + ' ' + t('orderCreation.schedulingErrorGoBack')
    }
    if (/template validation failed/i.test(message)) {
      return t('orderCreation.appointmentInPast') + ' ' + t('orderCreation.schedulingErrorGoBack')
    }
    return null
  }

  function extractAdvanceTime(errorMsg: string): string {
    const match = errorMsg.match(/at least\s+([\d]+\s*\w+)/i)
    if (match) return match[1]
    const minsMatch = errorMsg.match(/(\d+)\s*minutes?/i)
    if (minsMatch) return formatMinutesAsHuman(parseInt(minsMatch[1], 10))
    return '24 hours'
  }

  function handleGoBackToSchedule() {
    setError(null)
    setCurrentStep(4)
    navigate('/create-order/appointments')
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-1/3" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )
    }

    if (error && !creating) {
      const isSchedulingError = error.includes(t('orderCreation.schedulingErrorGoBack'))

      if (isSchedulingError) {
        const isInPast = error.includes(t('orderCreation.appointmentInPast'))

        return (
          <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-700">
            <CardContent className="py-8">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                  {isInPast
                    ? <Clock className="h-7 w-7 text-amber-600 dark:text-amber-400" />
                    : <CalendarClock className="h-7 w-7 text-amber-600 dark:text-amber-400" />
                  }
                </div>
                <div className="space-y-2 max-w-md">
                  <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300">
                    {isInPast
                      ? t('orderCreation.appointmentInPast')
                      : t('orderCreation.appointmentTooSoon', { hours: extractAdvanceTime(error) })
                    }
                  </h3>
                </div>
                <Button
                  onClick={handleGoBackToSchedule}
                  className="mt-2 gap-2"
                  size="lg"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('orderCreation.scheduleAppointments')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      }

      return (
        <Card className="border-destructive">
          <CardContent className="py-8">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-destructive" />
              </div>
              <div className="space-y-2 max-w-md">
                <h3 className="text-lg font-semibold text-destructive">
                  {t('common.error')}
                </h3>
                <p className="text-muted-foreground">{error}</p>
              </div>
              <Button onClick={loadBeneficiaries} variant="outline" className="mt-2">
                {t('common.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className="space-y-4">
        {/* Divider */}
        {beneficiaries.length > 0 && (
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-background text-muted-foreground">
                {t('orderCreation.orSelectFamilyMember')}
              </span>
            </div>
          </div>
        )}

        {/* Family Members */}
        {beneficiaries.map((beneficiary) => (
          <Card
            key={beneficiary.id}
            className={`cursor-pointer transition-all ${
              selectedBeneficiary?.id === beneficiary.id
                ? 'border-primary shadow-md ring-2 ring-primary/20'
                : 'hover:shadow-md hover:border-primary/50'
            }`}
            onClick={() =>
              handleSelectBeneficiary(
                beneficiary,
                beneficiary.id === orderSubjectProfileId ||
                  beneficiary.relationship?.toLowerCase() === 'self'
              )
            }
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{beneficiary.fullName}</p>
                    <p className="text-sm text-muted-foreground">
                      {beneficiary.relationship || t('orderCreation.beneficiary')}
                      {beneficiary.dateOfBirth && (
                        <span className="ms-2">
                          • {new Date(beneficiary.dateOfBirth).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {selectedBeneficiary?.id === beneficiary.id && (
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Add Beneficiary */}
        <Card
          className="cursor-pointer hover:shadow-md border-dashed hover:border-primary/50 transition-all"
          onClick={() => setAddBeneficiaryOpen(true)}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="h-12 w-12 rounded-full border-2 border-dashed flex items-center justify-center">
                <UserPlus className="h-6 w-6" />
              </div>
              <p className="font-medium">
                {t('orderCreation.addNewFamilyMember')}
              </p>
            </div>
          </CardContent>
        </Card>

        <AddBeneficiaryModal
          isOpen={addBeneficiaryOpen}
          onClose={() => setAddBeneficiaryOpen(false)}
          clientId={clientProfileId}
          businessClientProfileId={businessClientProfileId}
          onSuccess={async (beneficiary) => {
            const rows = await loadBeneficiaries()
            const match =
              rows.find((r) => r.id && beneficiary.id && r.id === beneficiary.id) ||
              rows.find(
                (r) =>
                  r.fullName.trim().toLowerCase() === beneficiary.fullName.trim().toLowerCase()
              ) ||
              rows[rows.length - 1]
            if (match) {
              setSelectedBeneficiary(match)
              setIsSelfBeneficiary(false)
            }
          }}
        />
      </div>
    )
  }

  return (
    <OrderCreationLayout
      titleKey="orderCreation.selectBeneficiary"
      descriptionKey="orderCreation.selectBeneficiaryDescription"
      step={5}
      backPath="/create-order/appointments"
    >
      <div className="max-w-2xl mx-auto">
        {/* Client Info */}
        <Card className="mb-6 bg-muted/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  {t('orderCreation.orderFor')}
                </p>
                <p className="font-semibold">{clientName}</p>
              </div>
              <Badge variant="secondary">
                {t('orderCreation.stepOf', { step: 5, total: 7 })}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {renderContent()}

        {/* Continue Button */}
        {!loading && !error && (
          <div className="mt-6 space-y-3">
            {canSkip && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t('orderCreation.beneficiaryNotRequired')}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end gap-3">
              {canSkip && (
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  disabled={!!creating}
                  size="lg"
                >
                  {creating === 'skip' ? (
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  ) : (
                    <SkipForward className="me-2 h-4 w-4" />
                  )}
                  {t('orderCreation.skipBeneficiary')}
                </Button>
              )}
              <Button
                onClick={handleContinue}
                disabled={!selectedBeneficiary || !!creating}
                size="lg"
                className="min-w-[200px]"
              >
                {creating === 'continue' ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t('orderCreation.creatingOrder')}
                  </>
                ) : (
                  t('common.continue')
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </OrderCreationLayout>
  )
}
